import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { useThemeContext } from '../../../utils/providers/theme-context';

type CSSWithVars = React.CSSProperties & {
  [key: `--${string}`]: string | number | undefined;
};

type ColorToken =
  | 'activeText'
  | 'normalText'
  | 'secondLayer'
  | 'border'
  | 'borderPrimary'
  | 'inputBorder'
  | 'icon'
  | 'inactiveText'
  | 'dropdownText'
  | 'baseLayer'
  | 'firstLayer'
  | 'thirdLayer'
  | 'datagridBg';

interface CustomInputProps {
  id?: string;
  name?: string;
  value?: string | number;
  defaultValue?: string | number;
  label?: string | React.ReactNode;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  placeholder?: string;
  numeric?: boolean;
  multiline?: boolean;
  /** Visible lines of a multiline input. */
  rows?: number;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  wrapperClass?: string;
  labelClass?: string;
  autoComplete?: string;
  type?: string;
  error?: string;
  autoFocus?: boolean;
  required?: boolean;
  onFocus?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  maxLength?: number;
  disabled?: boolean;
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode'];
  labelTruncate?: boolean;
  /** NEW: make the field read-only without disabling it */
  readOnly?: boolean;

  /** NEW: small descriptive text under the field */
  helperText?: string | React.ReactNode;
  helperClass?: string;
  /** Extra inline style merged onto the input (useful for overriding CSS vars like --in-bg). */
  style?: React.CSSProperties;
}

const Input = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  CustomInputProps
>((props, ref) => {
  const {
    id,
    name,
    value,
    defaultValue,
    onChange,
    label,
    placeholder = '',
    numeric = false,
    multiline = false,
    rows = 3,
    size = 'medium',
    className,
    wrapperClass,
    labelClass,
    autoComplete,
    type = 'text',
    error,
    autoFocus,
    required,
    onFocus,
    onBlur,
    maxLength,
    disabled,
    inputMode,
    labelTruncate = true,
    readOnly,
    helperText,
    helperClass,
    style
  } = props;

  const themeContext = useThemeContext();
  const theme = themeContext?.theme ?? 'light';
  const brandColor = themeContext?.brandColor ?? null;
  const getColour = themeContext?.getColour;

  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const safeColour = (token: ColorToken): string =>
    getColour?.(token) ?? 'inherit';

  const active = brandColor ?? safeColour('activeText');
  const text = safeColour('normalText'); // fg
  const second = safeColour('secondLayer'); // surface
  const borderBase = safeColour('border'); // border
  const borderHover = safeColour('border'); // hover border
  const placeholderColor = safeColour('inactiveText');
  const helperColor = placeholderColor; // subtle helper tone
  const disabledBg = pick('rgba(17,24,39,0.03)', 'rgba(255,255,255,0.06)');
  const ring = active;

  // Use controlled component pattern when value is provided
  const isControlled = value !== undefined;

  const [internalValue, setInternalValue] = useState<string>(() => {
    if (value !== undefined) return String(value);
    if (defaultValue !== undefined) return String(defaultValue);
    return '';
  });

  useEffect(() => {
    if (value !== undefined) setInternalValue(String(value));
  }, [value]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (isControlled) {
      setInternalValue(e.target.value);
    }
    onChange(e);
  };

  const sizeClasses = useMemo(() => {
    switch (size) {
      case 'small':
        return 'h-9 text-sm';
      case 'large':
        return 'h-12 text-base';
      default:
        return 'h-10 text-sm';
    }
  }, [size]);

  const baseInputClass = cn(
    'block w-full rounded-2xl border transition-all appearance-none focus:ring-0! focus-visible:outline-none!',
    'bg-[var(--in-bg)] text-[var(--in-fg)] placeholder-[var(--in-ph)]',
    'border-[var(--in-border)] hover:border-[var(--in-border-hover)]',
    'focus:outline-none focus:ring-1 focus:ring-[var(--in-ring)] focus:border-[var(--in-ring)]',
    'disabled:bg-[var(--in-disabled-bg)] disabled:opacity-50 disabled:!shadow-none',
    className?.includes('text-center') ? 'text-center' : 'text-left',
    className
  );

  const inputClass = cn(
    baseInputClass,
    'px-3',
    sizeClasses,
    'shadow-[var(--in-shadow)] focus:shadow-[var(--in-focus-shadow)]'
  );

  const textareaClass = cn(
    baseInputClass,
    'px-3 py-2 h-auto leading-5 text-sm',
    'min-h-[2.5rem] resize-y',
    'shadow-[var(--in-shadow)] focus:shadow-[var(--in-focus-shadow)]'
  );

  const labelClasses = cn('block text-sm font-semibold mb-2 pl-1', labelClass);

  const inputShadow = '0 2px 6px rgba(0,0,0,0.10), 0 1px 3px rgba(0,0,0,0.06)';
  const inputFocusShadow =
    '0 4px 12px rgba(0,0,0,0.14), 0 2px 5px rgba(0,0,0,0.08)';

  const styleVars: CSSWithVars = {
    '--in-bg': second,
    '--in-fg': text,
    '--in-ph': placeholderColor,
    '--in-border': borderBase,
    '--in-border-hover': borderHover,
    '--in-ring': ring,
    '--in-disabled-bg': disabledBg,
    '--in-shadow': inputShadow,
    '--in-focus-shadow': inputFocusShadow
  };

  const helperId = helperText ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const ariaDescribedBy =
    [helperId, errorId].filter(Boolean).join(' ') || undefined;

  const commonProps = {
    id,
    name,
    placeholder,
    ...(isControlled ? { value: internalValue } : { defaultValue }),
    onChange: handleChange,
    autoComplete,
    autoFocus,
    required,
    onFocus,
    onBlur,
    maxLength,
    className: inputClass,
    style: { ...styleVars, ...style },
    'aria-invalid': !!error || undefined,
    'aria-describedby': ariaDescribedBy,
    disabled,
    readOnly
  };

  return (
    <div className={wrapperClass ?? 'w-full'}>
      {label && (
        <label htmlFor={id} className={labelClasses}>
          <span
            className={cn(
              'inline-flex items-center gap-0.5',
              labelTruncate && 'max-w-full'
            )}
            title={typeof label === 'string' ? label : undefined}
          >
            <span
              className={cn(
                labelTruncate &&
                  'overflow-hidden text-ellipsis whitespace-nowrap'
              )}
            >
              {label}
            </span>
            {required && (
              <span className="text-red-500 flex-shrink-0" aria-hidden="true">
                *
              </span>
            )}
          </span>
        </label>
      )}

      {multiline ? (
        <textarea
          rows={rows}
          ref={ref as React.Ref<HTMLTextAreaElement>}
          {...commonProps}
          className={textareaClass}
        />
      ) : (
        <input
          type={type}
          inputMode={inputMode ?? (numeric ? 'numeric' : undefined)}
          pattern={numeric ? '[0-9]*' : undefined}
          ref={ref as React.Ref<HTMLInputElement>}
          {...commonProps}
        />
      )}

      {/* Helper */}
      {helperText && !error && (
        <p
          id={helperId}
          className={cn('text-xs mt-1', helperClass)}
          style={{ color: helperColor }}
        >
          {helperText}
        </p>
      )}

      {/* Error */}
      {error && (
        <p
          id={errorId}
          className="text-sm mt-1"
          style={{ color: getColour?.('danger') ?? 'red' }}
        >
          {error}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
